import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import * as labels from "../app_utils/constants/Labels";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  name: string;
  description: string;
  provider: string;
  appId: string;
  apiKey: string;
  merchantId: string;
  callbackSecret: string;
  baseUrl: string;
  priority: number;
  status: string;
}

const PAYMENT_PROVIDERS = [{ label: "KaCyber", value: "KACYBER" }];

const GATEWAY_STATUSES = [
  { label: "Active", value: "Active" },
  { label: "Deactivated", value: "Deactivated" },
];

const emptyRecord = (): RecordType => ({
  name: "",
  description: "",
  provider: "KACYBER",
  appId: "",
  apiKey: "",
  merchantId: "",
  callbackSecret: "",
  baseUrl: "",
  priority: 1,
  status: "Deactivated",
});

const BankTransferGatewayFormDialog = (props: ModalType) => {
  const [record, setRecord] = useState<RecordType | null>(
    props?.record
      ? {
          name: props.record.name || "",
          description: props.record.description || "",
          provider: props.record.provider || "KACYBER",
          appId: props.record.appId || "",
          apiKey: props.record.apiKey || "",
          merchantId: props.record.merchantId || "",
          callbackSecret: props.record.callbackSecret || "",
          baseUrl: props.record.baseUrl || "",
          priority: props.record.priority || 1,
          status: props.record.status || "Deactivated",
        }
      : emptyRecord()
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const message = useRef<any>();

  useEffect(() => {
    setRecord(
      props?.record
        ? {
            name: props.record.name || "",
            description: props.record.description || "",
            provider: props.record.provider || "KACYBER",
            appId: props.record.appId || "",
            apiKey: props.record.apiKey || "",
            merchantId: props.record.merchantId || "",
            callbackSecret: props.record.callbackSecret || "",
            baseUrl: props.record.baseUrl || "",
            priority: props.record.priority || 1,
            status: props.record.status || "Deactivated",
          }
        : emptyRecord()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.record]);

  const clearForm = () => {
    setRecord(null);
  };

  const validateForm = () => {
    if (!record?.name || !record?.provider || !record?.appId || !record?.apiKey) {
      MessageUtils.showErrorMessage(message, "Please fill in all required fields");
      return false;
    }
    return true;
  };

  const saveGateway = () => {
    if (!validateForm()) return;
    setIsSaving(true);
    const url = props?.record
      ? "/api/v1/bank-transfer-gateways/" + props.record.id
      : "/api/v1/bank-transfer-gateways";
    const method = props?.record ? "putRequestWithJsonResponse" : "postRequestWithJsonResponse";

    new BaseApiServiceImpl(url)
      [method](record)
      .then(async (response) => {
        setIsSaving(false);
        MessageUtils.showSuccessMessage(
          props?.messageRef,
          labels.LABEL_RECORD_SAVED_SUCCESSFULLY
        );
        closeDialog();
        props?.reloadFn();
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const closeDialog = () => {
    clearForm();
    props.toggle();
  };

  const gatewayDialogFooter = (
    <>
      <Button
        label={labels.LABEL_CANCEL}
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={closeDialog}
      />
      <Button
        label={labels.LABEL_SAVE}
        icon={PrimeIcons.SAVE}
        className="p-button-secondary"
        onClick={saveGateway}
        loading={isSaving}
      />
    </>
  );

  const isEdit = !!props?.record;

  return (
    <Dialog
      visible={props.isOpen}
      header={isEdit ? "Edit Bank Transfer Gateway" : "New Bank Transfer Gateway"}
      footer={gatewayDialogFooter}
      modal
      className="p-fluid"
      onHide={closeDialog}
      style={{ width: "50vw" }}
    >
      <div className="grid">
        <div className="col-12">
          <Messages ref={message} style={{ width: "100%" }} />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="name">Name *</label>
          <InputText
            id="name"
            name="gateway-display-name"
            autoComplete="off"
            value={record?.name || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, name: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="provider">Provider</label>
          <Dropdown
            id="provider"
            value={record?.provider || "KACYBER"}
            options={PAYMENT_PROVIDERS}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, provider: e.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="appId">App ID *</label>
          <InputText
            id="appId"
            name="kacyber-app-id"
            autoComplete="off"
            value={record?.appId || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, appId: e.target.value }))}
            className="w-full mt-1"
          />
          <small className="p-disabled">
            Your KaCyber merchant App ID. On edit, leave blank to keep the saved one.
          </small>
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="apiKey">API Key *</label>
          <InputText
            id="apiKey"
            name="kacyber-api-key"
            type="password"
            autoComplete="new-password"
            value={record?.apiKey || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, apiKey: e.target.value }))}
            className="w-full mt-1"
          />
          <small className="p-disabled">
            Your KaCyber API key. Never displayed after saving; leave blank on edit to keep it.
          </small>
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="merchantId">Merchant ID</label>
          <InputText
            id="merchantId"
            name="kacyber-merchant-id"
            autoComplete="off"
            value={record?.merchantId || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, merchantId: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="callbackSecret">Callback Secret</label>
          <InputText
            id="callbackSecret"
            name="kacyber-callback-secret"
            autoComplete="off"
            value={record?.callbackSecret || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, callbackSecret: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12">
          <label htmlFor="description">Description</label>
          <InputText
            id="description"
            autoComplete="off"
            value={record?.description || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, description: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12">
          <label htmlFor="baseUrl">Base URL</label>
          <InputText
            id="baseUrl"
            autoComplete="off"
            value={record?.baseUrl || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, baseUrl: e.target.value }))}
            className="w-full mt-1"
          />
          <small className="p-disabled">
            Leave blank to use the server default.
          </small>
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="priority">Priority</label>
          <InputText
            id="priority"
            type="number"
            value={record?.priority || 1}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, priority: parseInt(e.target.value) || 1 }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="status">Status</label>
          <Dropdown
            id="status"
            value={record?.status || "Deactivated"}
            options={GATEWAY_STATUSES}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, status: e.value }))}
            className="w-full mt-1"
          />
          <small className="p-disabled">
            The highest-priority Active gateway is used for all bank transfers.
          </small>
        </div>
      </div>
    </Dialog>
  );
};

export default BankTransferGatewayFormDialog;
